import React, { useState, useEffect } from 'react';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { practitioners as localPractitioners, Practitioner } from '@/data/practitioners';
import { MapPin, Award, Calendar, CheckCircle2 } from 'lucide-react';
import { trackEvent } from '@/utils/analytics';
import { Button } from '@/components/ui/Button';
import { BookCoachingModal } from '@/components/ui/BookCoachingModal';
import { useRegion, isSouthAfrica } from '@/contexts/RegionContext';
import { LazyImage } from '@/components/ui/LazyImage';

const getTargetCurrency = (code?: string | null): 'ZAR' | 'USD' => {
  return isSouthAfrica(code) ? 'ZAR' : 'USD';
};

export function PractitionerProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { regionCode: currentRegion } = useRegion();
  const targetCurrency = getTargetCurrency(currentRegion);
  
  const [exchangeRates, setExchangeRates] = useState<Record<string, number>>({ USD: 1, ZAR: 18, GBP: 0.8, EUR: 0.95 });
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const [practitioner, setPractitioner] = useState<Practitioner | null>(
    localPractitioners.find(p => p.id === id) || null
  );
  const [isLoadingProfile, setIsLoadingProfile] = useState(!practitioner);

  useEffect(() => {
    fetch('https://open.er-api.com/v6/latest/USD')
      .then(res => res.json())
      .then(data => {
        if (data && data.rates) {
          setExchangeRates(data.rates);
        }
      })
      .catch(err => {
        console.error('Failed to fetch exchange rates', err);
      });

    if (!practitioner && id) {
      const fetchPractitioner = async () => {
        try {
          const res = await fetch(`/api/public/practitioners/${encodeURIComponent(id)}`);
          if (res.ok) {
            const data = await res.json();
            if (data) {
              setPractitioner(data as Practitioner);
            }
          }
        } catch (error) {
          console.error("Error fetching practitioner:", error);
        } finally {
          setIsLoadingProfile(false);
        }
      };
      fetchPractitioner();
    }
  }, [id]);

  if (isLoadingProfile) {
    return <div className="pt-32 pb-24 min-h-screen bg-ground-alt flex justify-center"><div className="animate-pulse">Loading profile...</div></div>;
  }

  if (!practitioner) {
    return <Navigate to="/404" replace />;
  }



  const rates = practitioner.approvedRates || {};
  const oneOnOneRateVal = rates.oneOnOneRate || null;
  const teamRateVal = rates.teamRate || null;

  return (
    <main className="pt-32 pb-24 min-h-screen bg-ground-alt">
      <Helmet>
        <title>{practitioner.name} | FIVE LENS Practitioner</title>
        <meta name="description" content={`Profile for ${practitioner.name}, certified FIVE LENS practitioner in ${practitioner.location}.`} />
      </Helmet>

      <div className="max-w-4xl mx-auto px-6 lg:px-12">
        <Breadcrumb items={[{ label: 'For Coaches and Practitioners', href: '/practitioners' }, { label: 'Directory', href: '/practitioner-directory' }, { label: practitioner.name }]} />
        
        <div className="bg-white border border-ground-deep overflow-hidden relative">
          <div className="grid md:grid-cols-3">
            {(practitioner.photoUrl || (practitioner as any).imageUrl) && (
              <div className="md:col-span-1 bg-ground-deep aspect-square md:aspect-auto h-64 md:h-full relative">
                <LazyImage 
                  src={practitioner.photoUrl || (practitioner as any).imageUrl} 
                  alt={practitioner.name} 
                  className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
                />
              </div>
            )}
            
            <div className={`p-8 lg:p-12 ${(practitioner.photoUrl || (practitioner as any).imageUrl) ? 'md:col-span-2' : 'md:col-span-3'}`}>
              <div className="flex flex-wrap gap-2 mb-4">
                <div className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 rounded-sm border bg-sky-100 text-sky-900 border-sky-300">
                  <Award className="w-3.5 h-3.5 text-sky-700" />
                  {practitioner.certificationLevel || 'Level 1 Practitioner'}
                </div>
                {(practitioner.certifications || []).map(cert => (
                  <div key={cert} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-1 rounded-sm border bg-rule-3/20 text-rule-3 border-rule-3/40">
                    <CheckCircle2 className="w-3 h-3" />
                    {cert} Certified
                  </div>
                ))}
              </div>
              
              <h1 className="text-4xl font-serif text-ink mb-4">{practitioner.name}</h1>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8 pb-8 border-b border-ground-deep">
                <div className="flex items-center gap-2 text-sm text-ink/70">
                  <MapPin className="w-4 h-4 text-rule-3 shrink-0" />
                  {practitioner.location}
                </div>
              </div>

              <div className="mb-8">
                <h3 className="text-sm font-bold uppercase tracking-widest text-ink mb-4">Specialisms</h3>
                <div className="flex flex-wrap gap-2">
                  {(practitioner.specialisms || (practitioner as any).specialism || []).map(s => (
                    <span key={s} className="text-xs bg-ground-alt text-ink px-3 py-1.5 rounded-sm border border-ground-deep">
                      {s}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mb-10">
                <h3 className="text-sm font-bold uppercase tracking-widest text-ink mb-4">Biography</h3>
                <p className="text-ink/80 leading-relaxed">
                  {practitioner.bio}
                </p>
              </div>

              <div className="pt-8 border-t border-ground-deep">
                <div className="bg-ground-alt p-6 border border-ground-deep">
                  <h3 className="text-lg font-serif text-ink mb-2">Book Coaching</h3>
                  <p className="text-sm text-ink/70 mb-6">
                    Partner with {practitioner.name} for specialized coaching through the FIVE LENS platform. 
                  </p>
                  
                  <div className="flex flex-col gap-4 mb-8">
                    {oneOnOneRateVal && (
                      <div className="flex items-center justify-between border-b border-ground-deep/50 pb-4">
                        <div>
                          <p className="font-semibold text-ink text-sm">1-on-1 Executive & Development Coaching</p>
                          <p className="text-xs text-ink/60">Individual personal debrief and coaching session</p>
                        </div>
                        <div className="flex items-center gap-1 text-sm text-ink font-bold bg-white px-3 py-1 border border-ground-deep rounded-sm">
                          <span>R{Number(oneOnOneRateVal).toLocaleString()} / session</span>
                        </div>
                      </div>
                    )}
                    
                    {teamRateVal && (
                      <div className="flex items-center justify-between border-b border-ground-deep/50 pb-4">
                        <div>
                          <p className="font-semibold text-ink text-sm">Team Facilitation & Alignment Workshop</p>
                          <p className="text-xs text-ink/60">Level 2 Team Facilitator full-day workshop</p>
                        </div>
                        <div className="flex items-center gap-1 text-sm text-ink font-bold bg-white px-3 py-1 border border-ground-deep rounded-sm">
                          <span>R{Number(teamRateVal).toLocaleString()} / day</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {practitioner.bookingUrl ? (
                    <a 
                      href={practitioner.bookingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 w-full bg-ink text-white px-8 py-3 uppercase tracking-widest text-sm font-bold hover:bg-ink/70 transition-colors"
                      onClick={() => trackEvent('practitioner_calendar_click', { practitioner_id: practitioner.id })}
                    >
                      <Calendar className="w-4 h-4" />
                      Book via Calendar
                    </a>
                  ) : (
                    <Button onClick={() => setIsModalOpen(true)} className="w-full">
                      Request Coaching
                    </Button>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      <BookCoachingModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        practitioner={practitioner}
      />
    </main>
  );
}
